import { Table } from "react-bootstrap";
import formatDollars from "../../../../../utilities/currency-formatter";
import styles from "../../../../../styles/screening.module.css";
import React from "react";

const ExpenditurePrimarySummary = ({ items, expenditureTypeMap, pvavalue }) => {
    const getFloatSafe = (x) => {
        if (x != null && !isNaN(x)) return parseFloat(x);
        return 0.0;
    };

    const sumByType = (arr = []) =>
        arr.reduce(
            (acc, current) => {
                const typeKey = current.expendituretypeid;
                const amount = getFloatSafe(current.amount);

                if (!acc[typeKey]) acc[typeKey] = 0;
                acc[typeKey] += amount;
                acc.total += amount;

                return acc;
            },
            { total: 0 }
        );

    const sumRefunds = (arr = []) =>
        arr.reduce((sum, c) => sum + getFloatSafe(c.amount), 0);

    const proCardAmounts = sumByType(items.procard || []);
    const checkAmounts = sumByType(items.check || []);
    const refundTotal = sumRefunds(items.refund || []);

    const total = proCardAmounts.total + checkAmounts.total - refundTotal;
    
    const uniqueTypes = Object.keys(expenditureTypeMap).sort((a, b) => {
        const nameA = expenditureTypeMap[a]?.name ?? a;
        const nameB = expenditureTypeMap[b]?.name ?? b;
        return nameA.localeCompare(nameB);
    });

    const evaluateLimits = (typeKey) => {
        const info = expenditureTypeMap[typeKey] || {};
        const spent =
            (proCardAmounts[typeKey] || 0) +
            (checkAmounts[typeKey] || 0);

        let maxExceeded = false;
        let pvaExceeded = false;

        if (info.maxlimitrate != null) {
            const maxLimit = info.maxlimitrate;
            maxExceeded = spent > maxLimit;
        }

        if (pvavalue != null && info.pvalimitrate != null) {
            const pvaLimit = pvavalue * (info.pvalimitrate / 100);
            pvaExceeded = spent > pvaLimit;
        }

        return { spent, maxExceeded, pvaExceeded };
    };

    const getLimitWarning = (typeKey) => {
        const { maxExceeded, pvaExceeded } = evaluateLimits(typeKey);

        if (!maxExceeded && !pvaExceeded) return "";

        if (maxExceeded && pvaExceeded) return "MAX & PVA limits exceeded";
        if (maxExceeded) return "MAX limit exceeded";
        if (pvaExceeded) return "PVA limit exceeded";

        return "";
    };

    const violations = uniqueTypes
        .map((typeKey) => {
            const warn = getLimitWarning(typeKey);
            if (!warn) return null;
            const name = expenditureTypeMap[typeKey]?.name ?? typeKey;
            return `${name}: ${warn}`;
        })
        .filter(Boolean);

    const limitSummaries = uniqueTypes
        .map((typeKey) => {
            const info = expenditureTypeMap[typeKey];
            if (!info) return null;

            const name = info.name ?? typeKey;

            const parts = [];
            if (info.maxlimitrate != null)
                parts.push(`MAX ${formatDollars(info.maxlimitrate)}`);
            if(info.pvalimitrate !== null){
                if(pvavalue !== null){
                    parts.push(
                        `PVA ${info.pvalimitrate}% (${formatDollars(
                            pvavalue * (info.pvalimitrate / 100)
                        )})`
                    );
                }   
                else{
                    parts.push(
                        `PVA ${info.pvalimitrate}%`
                    );
                }
            }

            if (parts.length === 0) return `${name}: No limits`;

            return `${name}: ${parts.join(" | ")}`;
        })
        .filter(Boolean);

    return (
        <div className="clientCard">
            <div className="clientCardInner">
                <h4 className="text-center">Expenditure Worksheet</h4>

                <Table bordered striped size="sm" className={styles.responsiveTable}>
                    <thead>
                        <tr className="text-center align-middle">
                            <th colSpan={uniqueTypes.length}>
                                PROCARD ({formatDollars(proCardAmounts.total)})
                            </th>
                            <th rowSpan={2}></th>
                            <th colSpan={uniqueTypes.length}>
                                CHECK ({formatDollars(checkAmounts.total)})
                            </th>
                            <th rowSpan={2}></th>
                            <th rowSpan={2}>REFUND ({formatDollars(refundTotal)})</th>
                            <th rowSpan={2}></th>
                            <th rowSpan={2}>
                                NET EXPENDED
                                {limitSummaries.map((txt, i) => (
                                    <React.Fragment key={i}>
                                    <br />
                                    <span style={{ fontSize: "0.75em" }} className="text-muted fw-normal text-wrap">{txt}</span>
                                    </React.Fragment> 
                                ))}

                            </th>
                        </tr>

                        <tr className="text-center align-middle">
                            {uniqueTypes.map((t) => (
                                <th key={`pc-h-${t}`} className="fw-normal">
                                    {expenditureTypeMap[t]?.name ?? t}
                                </th>
                            ))}
                            {uniqueTypes.map((t) => (
                                <th key={`c-h-${t}`} className="fw-normal">
                                    {expenditureTypeMap[t]?.name ?? t}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="text-center align-middle">
                        <tr>
                            {uniqueTypes.map((typeKey) => (
                                <td key={`pc-${typeKey}`}>
                                    {formatDollars(proCardAmounts[typeKey] || 0)}
                                </td>
                            ))}
                            <td className="fw-bold">+</td>
                            {uniqueTypes.map((typeKey) => (
                                <td key={`c-${typeKey}`}>
                                    {formatDollars(checkAmounts[typeKey] || 0)}
                                </td>
                            ))}
                            <td className="fw-bold">-</td>
                            <td>{formatDollars(refundTotal)}</td>
                            <td className="fw-bold">=</td>
                            <td className={`${violations.length > 0 ? 'text-danger' : ''}`}>
                                <span className={`fw-bolder fs-4`}>
                                    {formatDollars(total)}
                                </span>
                                {violations.map((item,idx) => (
                                    <span key={idx} className="d-block" style={{ fontSize: "0.75em" }}>
                                        {item}
                                    </span>
                                ))}
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default ExpenditurePrimarySummary;




